import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "../i18n";
import type { AccountSummary } from "../types";

/**
 * Which client you are working in, and how to change it (Bu Mishary, 2026-08-20:
 * "the top left part can be clicked and a list of available clients will be
 * there to choose from").
 *
 * WHY THE NAME STAYS ON SCREEN. This replaced a labelled <select> below the
 * brand block, and that control existed for a reason worth keeping: approving
 * work against the wrong client is the expensive mistake here, so which client
 * you are in must never be more than a glance away. So the NAME is still always
 * visible — only the LIST moved into a menu. Collapsing the name into a menu too
 * would trade a real safeguard for tidiness.
 *
 * A person with one client gets no button at all: no affordance, nothing to
 * click, no hint that other clients exist. The list itself is already scoped by
 * the server — see server/assignments.ts — so this renders what it is given and
 * decides nothing.
 */
export default function AccountSwitcher({
  accounts,
  activeId,
  accountName,
  onSwitch,
}: {
  accounts: AccountSummary[];
  activeId: string;
  accountName: string;
  onSwitch: (accountId: string) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // A filter only once the list is long enough to need one. Below that it is a
  // control that costs a keystroke and saves nothing.
  const searchable = accounts.length > 8;

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return accounts;
    return accounts.filter((a) => a.name.toLowerCase().includes(term));
  }, [accounts, query]);

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    if (searchable) searchRef.current?.focus();

    // Escape and click-outside, because a menu that can only be closed by picking
    // something forces a choice the reader may not want to make.
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    const onClick = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, searchable]);

  const identity = (
    <>
      <div className="brand-mark" aria-hidden="true">{accountName.charAt(0).toUpperCase()}</div>
      <div className="brand-copy">
        <strong dir="auto">{accountName}</strong>
        <span>{t("shell.productionCredits")}</span>
      </div>
    </>
  );

  // One client, or none to switch to: no button, no chevron, no menu.
  if (accounts.length < 2) return <div className="brand-identity">{identity}</div>;

  return (
    <div className="brand-switcher" ref={boxRef}>
      <button
        type="button"
        className="brand-identity brand-identity-button"
        onClick={() => setOpen((was) => !was)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("shell.switchClient", { name: accountName })}
      >
        {identity}
        <ChevronsUpDown size={16} className="brand-chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="brand-menu" role="listbox" aria-label={t("shell.chooseClient")}>
          {searchable && (
            <div className="brand-menu-search">
              <Search size={15} aria-hidden="true" />
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("shell.findClient")}
                aria-label={t("shell.findClient")}
              />
            </div>
          )}
          <ul>
            {shown.map((account) => {
              const current = account.id === activeId;
              return (
                <li key={account.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={current}
                    className={current ? "is-current" : undefined}
                    onClick={() => {
                      setOpen(false);
                      // Switching to where you already are reloads the whole
                      // workspace for no change. Say nothing and close.
                      if (!current) onSwitch(account.id);
                    }}
                  >
                    <span dir="auto">{account.name}</span>
                    {current && <Check size={15} aria-hidden="true" />}
                  </button>
                </li>
              );
            })}
            {shown.length === 0 && <li className="brand-menu-empty">{t("shell.noClientMatch")}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
